import { useMediaLibrary } from "@/hooks/use-media-library";
import { FileIcon, ImageIcon, UploadCloud, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";

export function MediaUploader({
  state,
}: {
  state: ReturnType<typeof useMediaLibrary>;
}) {
  const { files, uploading, fileProgresses, validateAndSet, removeFile, doUpload, cancelUpload } =
    state;
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);

  // Map of file.name → object URL for image previews
  const previewUrls = useRef<Map<string, string>>(new Map());

  const getPreviewUrl = (f: File): string | null => {
    if (!f.type.startsWith("image/")) return null;
    if (!previewUrls.current.has(f.name)) {
      previewUrls.current.set(f.name, URL.createObjectURL(f));
    }
    return previewUrls.current.get(f.name)!;
  };

  const revokePreview = (name: string) => {
    const url = previewUrls.current.get(name);
    if (url) {
      URL.revokeObjectURL(url);
      previewUrls.current.delete(name);
    }
  };

  // Revoke all object URLs when the component unmounts
  useEffect(() => {
    return () => {
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  const onPickLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (picked && picked.length > 0) validateAndSet(Array.from(picked));
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files;
    if (dropped && dropped.length > 0) validateAndSet(Array.from(dropped));
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const overallProgress =
    files.length === 0
      ? 0
      : Math.round(
          Object.values(fileProgresses).reduce((a, b) => a + b, 0) / files.length
        );

  return (
    <>
      <div className="flex-1 flex flex-col min-h-0 overflow-auto bg-gradient-to-b from-slate-50/30 to-white">
        <div className="max-w-2xl mx-auto w-full p-3 sm:p-6 space-y-4">
          {/* Drag and Drop Area */}
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={cn(
              "rounded-xl border-2 border-dashed px-4 py-8 sm:p-12 text-center transition-all duration-200",
              dragOver
                ? "border-blue-400 bg-gradient-to-br from-blue-50/80 to-indigo-50/60 shadow-lg scale-[1.02]"
                : "border-slate-300 bg-gradient-to-br from-slate-50/60 to-white hover:border-slate-400 hover:bg-slate-50/80"
            )}
          >
            <div
              className={cn(
                "mx-auto mb-3 sm:mb-4 flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-full transition-all duration-200",
                dragOver
                  ? "bg-blue-100 shadow-md scale-110"
                  : "bg-white shadow-sm border border-slate-200"
              )}
            >
              <UploadCloud
                className={cn(
                  "h-6 w-6 sm:h-8 sm:w-8 transition-colors",
                  dragOver ? "text-blue-600" : "text-slate-600"
                )}
              />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">
              <span className="hidden sm:inline">Drag & drop files here, or </span>
              <label className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer font-semibold transition-colors">
                <span className="sm:hidden">Tap to browse</span>
                <span className="hidden sm:inline">browse</span>
                <Input
                  type="file"
                  multiple
                  onChange={onPickLocal}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </p>
            <p className="text-xs text-slate-500">
              Max 25MB per file. Images, videos, or documents.
            </p>
          </div>

          {/* File Queue */}
          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((f) => {
                const pct = fileProgresses[f.name];
                const isDone = pct === 100;
                const previewUrl = getPreviewUrl(f);
                return (
                  <div
                    key={f.name}
                    className="rounded-lg border border-slate-200 bg-white shadow-sm p-3 sm:p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Thumbnail / icon */}
                        <div className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-50">
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={f.name}
                              className="h-full w-full object-cover cursor-zoom-in"
                              onClick={() => setLightbox({ url: previewUrl, name: f.name })}
                            />
                          ) : (
                            <div className="h-full w-full grid place-items-center">
                              {f.type.startsWith("video/") || f.type.startsWith("audio/") ? (
                                <FileIcon className="h-5 w-5 sm:h-6 sm:w-6 text-slate-500" />
                              ) : (
                                <ImageIcon className="h-5 w-5 sm:h-6 sm:w-6 text-slate-500" />
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {f.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {(f.size / 1024 / 1024).toFixed(2)} MB ·{" "}
                            <span className="hidden xs:inline">
                              {f.type || "application/octet-stream"}
                            </span>
                          </div>
                          {uploading && pct != null && (
                            <Progress value={pct} className="h-1.5 mt-1.5 bg-slate-200" />
                          )}
                        </div>
                      </div>
                      {!uploading ? (
                        <Button
                          onClick={() => { revokePreview(f.name); removeFile(f.name); }}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0 hover:bg-slate-100"
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Badge
                          variant={isDone ? "default" : "secondary"}
                          className="flex-shrink-0 text-xs"
                        >
                          {isDone ? "Done" : pct != null ? `${Math.round(pct)}%` : "Queued"}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload Button and Overall Progress */}
          <div className="space-y-3">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2">
              {files.length > 0 && !uploading && (
                <span className="text-xs text-slate-500 self-center">
                  {files.length} file{files.length > 1 ? "s" : ""} queued
                </span>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:ml-auto">
                {uploading && (
                  <Button variant="outline" onClick={cancelUpload} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={doUpload}
                  disabled={files.length === 0 || uploading}
                  className="gap-2 w-full sm:w-auto"
                >
                  <UploadCloud className="h-4 w-4" />
                  {uploading
                    ? `Uploading… (${overallProgress}%)`
                    : files.length > 1
                    ? `Upload ${files.length} files`
                    : "Upload"}
                </Button>
              </div>
            </div>
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>Overall progress</span>
                  <span className="font-medium">{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-2.5 bg-slate-200" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
            />
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-slate-100 transition-colors"
              title="Close"
            >
              <X className="h-4 w-4 text-slate-700" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 text-center pb-3">
              <span className="inline-block bg-black/60 text-white text-xs px-3 py-1 rounded-full truncate max-w-xs">
                {lightbox.name}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
